import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Video, ClipboardList, FileText, Wallet, Info } from 'lucide-react';
import { api } from '../lib/api';
import type { Notification } from '../types';
import { timeAgo } from '../lib/format';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const fetchAll = async () => {
    try {
      const [list, count] = await Promise.all([
        api.get<Notification[]>('/notifications'),
        api.get<{ count: number }>('/notifications/unread-count'),
      ]);
      setItems(list);
      setUnread(count.count);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`);
      fetchAll();
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      fetchAll();
    } catch {
      // ignore
    }
  };

  const iconFor = (type: string) => {
    switch (type) {
      case 'meeting': return <Video className="h-4 w-4 text-primary" />;
      case 'assignment': return <ClipboardList className="h-4 w-4 text-primary" />;
      case 'report': return <FileText className="h-4 w-4 text-purple-600" />;
      case 'expense': return <Wallet className="h-4 w-4 text-green-600" />;
      default: return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-600"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-16 z-50 max-h-[480px] flex flex-col bg-white rounded-lg shadow-xl border border-gray-200 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-800">Notifications</h3>
            <button onClick={markAllRead} className="flex items-center text-xs text-primary hover:text-primary-dark">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">No notifications yet</div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                onClick={() => !n.is_read && markRead(n.id)}
                className={`px-4 py-3 border-b border-gray-100 flex space-x-3 ${n.is_read ? 'opacity-60' : 'bg-primary/10'}`}
              >
                <div className="mt-0.5">{iconFor(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <Link to={n.link || '#'} className="block" onClick={() => setOpen(false)}>
                    <p className="text-sm font-medium text-gray-800">{n.title}</p>
                    {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
